import { apiSlice } from "../services/apiSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getAccessToken } from "../features/auth/services/auth";


interface GetPagesQuery {
    tags?: string;       // optional tag filter
    query?: string; // optional status filter
    filter?: string; // optional status filter
}
export const organizationPortalApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({

        getOrganizationDetails: builder.query({
            query: (id: number, scope?: string) => ({
                url: `/detail-page/{scope}/${id}`,
            }),
        }),




    }),
    overrideExisting: true,
});

type OrgDetailsQueryOptions = {
    refetchOnMountOrArgChange?: boolean;
};

// Keeps the same calling style in components:
// useGetOrganizationDetailsQuery(id, scope, { refetchOnMountOrArgChange: true })
export const useGetOrganizationDetailsQuery = (
    id: number,
    scope: string = 'organization',
    options?: OrgDetailsQueryOptions
) => {
    const [data, setData] = useState<any>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState(0);

    const fetchDetails = useCallback(async (controller: AbortController) => {
        const token = getAccessToken();
        const headers: Record<string, string> = {
            "ngrok-skip-browser-warning": "69420",
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const baseUrl = `${process.env.REACT_APP_API_URL}/portal`;
        const response = await fetch(`${baseUrl}/detail-page/${scope}/${id}`, {
            method: "GET",
            headers,
            signal: controller.signal,
        });

        if (!response.ok) {
            throw new Error("Failed to fetch organization details");
        }

        return response.json();
    }, [id, scope]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const run = async () => {
            setIsFetching(true);
            if (data === undefined) {
                setIsLoading(true);
            }

            try {
                const payload = await fetchDetails(controller);
                if (!isMounted) return;
                setData(payload);
            } catch {
                if (!isMounted) return;
                setData(undefined);
            } finally {
                if (!isMounted) return;
                setIsLoading(false);
                setIsFetching(false);
            }
        };

        if (typeof id === "number" && !Number.isNaN(id)) {
            run();
        } else {
            setIsLoading(false);
            setIsFetching(false);
            setData(undefined);
        }

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [id, scope, reloadKey, fetchDetails]);

    // No separate mount-time reload is needed. The main effect already fetches
    // on mount and on arg changes (id/scope/reloadKey).

    const refetch = useCallback(() => {
        setReloadKey((prev) => prev + 1);
    }, []);

    return useMemo(
        () => ({
            data,
            isLoading,
            isFetching,
            refetch,
        }),
        [data, isLoading, isFetching, refetch]
    );
};
